
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { upsertProfile } from "@/services/profileService";
import { ProfileData } from "@/types/profile";
import { useProfileStorage } from "./useProfileStorage";

/**
 * Hook for handling profile save functionality (manual save only)
 */
export const useProfileSaving = (
  profile: ProfileData,
  setUploading: (uploading: boolean) => void
) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { updateStorageFields } = useProfileStorage();
  const [autoSaved, setAutoSaved] = useState<boolean>(false);
  const [saveAttempts, setSaveAttempts] = useState<number>(0);
  const [savingState, setSavingState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  
  // Manual save function
  const saveProfile = async () => {
    if (!user) return false;
    
    // Only save if there are required fields with content
    if (!profile.full_name?.trim() || !profile.username?.trim()) {
      toast({
        title: "Missing required fields",
        description: "Please fill out all required fields before saving",
        variant: "destructive",
      });
      return false;
    }

    try {
      setUploading(true);
      setSavingState('saving');
      setSaveAttempts(prev => prev + 1);
      console.log("Saving profile...", saveAttempts);
      
      const savedProfile = await upsertProfile(user.id, profile);
      
      if (savedProfile) {
        setAutoSaved(true);
        setSavingState('saved');
        
        // Update local storage cache with merged data to ensure completeness
        try {
          // Merge with current cache if it exists
          updateStorageFields(savedProfile);
        } catch (error) {
          console.error("Error updating profile cache:", error);
        }
        
        toast({
          title: "Profile saved",
          description: "Your changes have been saved successfully",
          variant: "default",
        });
        
        return true;
      }
      return false;
    } catch (error) {
      console.error("Save error:", error);
      setSavingState('error');
      toast({
        title: "Error saving profile",
        description: "There was a problem saving your profile",
        variant: "destructive"
      });
      return false;
    } finally {
      setUploading(false);
      
      // Reset the saving state after a delay to show visual feedback
      setTimeout(() => {
        setSavingState('idle');
      }, 2000);
    }
  };

  return { 
    autoSaved, 
    savingState, 
    setSavingState,
    saveProfile 
  };
};
